import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../lib/api';
import {
  PenLine, Sparkles, Save, Loader2, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronRight, Users, RotateCcw, Info,
} from 'lucide-react';

// Settings > Email signature.
//
// Every mail sent from the Inbox gets the signed-in person's signature added
// by the server, so nobody has to remember to type it. This screen is where
// that signature is written.
//
// Two rules this component must not break:
//   1. The preview must show what the RECIPIENT gets, not what the editor
//      holds. That is why the preview HTML comes back from the server, after
//      the server has cleaned it — if cleaning changes something, the person
//      sees the change here rather than discovering it in a sent mail.
//   2. An AI draft is never saved. It fills the editor; the person presses
//      Save. The Save button is the only thing that writes.

const errText = (e) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail) return JSON.stringify(detail);
  return e?.message || String(e);
};

// The same sandbox the Inbox uses to show a received mail: same-origin so the
// frame can be measured, and NO allow-scripts, so nothing inside can run.
function SignaturePreview({ html }) {
  const [height, setHeight] = useState(200);
  const srcDoc = useMemo(() => (
    '<!doctype html><html><head><meta charset="utf-8">'
    + '<base target="_blank">'
    + '<style>html,body{margin:0;padding:14px;background:#fff;color:#2b2b2b;'
    + "font-family:Outfit,Arial,Helvetica,sans-serif;font-size:14px;line-height:1.6;}"
    + 'body{overflow-wrap:break-word;word-break:break-word;}'
    + 'img{max-width:100%;height:auto;}table{max-width:100%;}a{color:#2e3a68;}'
    + '</style></head><body>' + (html || '') + '</body></html>'
  ), [html]);

  const fit = (event) => {
    try {
      const measured = event?.target?.contentDocument?.body?.scrollHeight;
      if (measured) setHeight(Math.min(Math.max(measured + 16, 120), 1200));
    } catch (ignored) {
      setHeight(320);
    }
  };

  return (
    <iframe
      title="Signature preview"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcDoc}
      onLoad={fit}
      style={{ width: '100%', border: 0, height, background: '#fff' }}
      data-testid="signature-preview-frame"
    />
  );
}

function Field({ label, hint, value, onChange, placeholder, type = 'text' }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white
                   text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-400"
      />
      {hint && <span className="block text-[11px] text-slate-400 mt-1">{hint}</span>}
    </label>
  );
}

function OptionCard({ option, active, onPick }) {
  return (
    <button
      type="button"
      onClick={() => onPick(option)}
      className={`text-left rounded-lg border p-3 transition ${
        active ? 'border-[#c9a84c] bg-[#faf7f0]' : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
      data-testid={`sig-option-${option.key}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-xs font-semibold text-slate-800">{option.label}</span>
        {active && <CheckCircle2 className="w-3.5 h-3.5 text-[#b08d36]" />}
      </div>
      {option.description && (
        <div className="text-[11px] text-slate-500 mb-2">{option.description}</div>
      )}
      <div className="text-[11px] leading-snug text-slate-600 whitespace-pre-line line-clamp-6">
        {(option.text || '').slice(0, 220)}
      </div>
    </button>
  );
}

export default function SignatureSettings() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [picking, setPicking] = useState(false);

  const [me, setMe] = useState({});
  const [isManager, setIsManager] = useState(false);
  const [people, setPeople] = useState([]);
  const [editingId, setEditingId] = useState('');   // '' means "me"

  const [enabled, setEnabled] = useState(true);
  const [html, setHtml] = useState('');
  const [dirty, setDirty] = useState(false);

  const [details, setDetails] = useState({
    full_name: '', job_title: '', phone: '', email: '', website: '',
  });

  const [options, setOptions] = useState([]);
  const [chosen, setChosen] = useState('');
  const [aiNote, setAiNote] = useState('');

  const [previewHtml, setPreviewHtml] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [cleaned, setCleaned] = useState(false);
  const [advanced, setAdvanced] = useState(false);

  const previewTimer = useRef(null);

  // ---------------------------------------------------------------- loading

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/mail-signature/me');
      const sig = r.data?.signature || {};
      const who = r.data?.me || {};
      setMe(who);
      setIsManager(!!r.data?.is_manager);
      setHtml(sig.html || '');
      setEnabled(sig.enabled !== false);
      setDetails({
        full_name: who.full_name || '',
        job_title: who.job_title || '',
        phone: who.phone || '',
        email: who.email || '',
        website: '',
      });
      setError('');
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isManager) return;
    api.get('/mail-signature/people')
      .then((r) => setPeople(r.data?.people || []))
      .catch(() => setPeople([]));
  }, [isManager]);

  // Switching to somebody else loads their signature; switching back reloads mine.
  const pickPerson = async (id) => {
    setEditingId(id);
    setNotice('');
    setError('');
    try {
      if (!id) { await load(); return; }
      const r = await api.get(`/mail-signature/user/${id}`);
      const sig = r.data?.signature || {};
      setHtml(sig.html || '');
      setEnabled(sig.enabled !== false);
      setDirty(false);
    } catch (e) {
      setError(errText(e));
    }
  };

  // ---------------------------------------------------------------- preview

  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    if (!html.trim()) {
      setPreviewHtml('');
      setPreviewText('');
      setCleaned(false);
      return undefined;
    }
    previewTimer.current = setTimeout(() => {
      api.post('/mail-signature/preview', { html })
        .then((r) => {
          setPreviewHtml(r.data?.email_html || '');
          setPreviewText(r.data?.email_text || '');
          setCleaned(!!r.data?.changed);
        })
        .catch((e) => setError(errText(e)));
    }, 450);
    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
  }, [html]);

  // ---------------------------------------------------------------- actions

  const payload = () => ({
    full_name: details.full_name, job_title: details.job_title,
    phone: details.phone, email: details.email, website: details.website,
    count: 3,
  });

  const showLayouts = async () => {
    setPicking(true);
    setError('');
    try {
      const r = await api.post('/mail-signature/styles', payload());
      setOptions(r.data?.options || []);
      setAiNote(r.data?.note || '');
    } catch (e) {
      setError(errText(e));
    } finally {
      setPicking(false);
    }
  };

  const draftWithAi = async () => {
    setDrafting(true);
    setError('');
    try {
      const r = await api.post('/mail-signature/draft', payload());
      setOptions(r.data?.options || []);
      setAiNote(r.data?.note || '');
    } catch (e) {
      setError(errText(e));
    } finally {
      setDrafting(false);
    }
  };

  const pickOption = (option) => {
    setChosen(option.key);
    setHtml(option.html || '');
    setDirty(true);
    setNotice('');
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const url = editingId ? `/mail-signature/user/${editingId}` : '/mail-signature/me';
      const r = await api.put(url, { html, enabled });
      const sig = r.data?.signature || {};
      setHtml(sig.html || '');
      setDirty(false);
      setNotice('Saved. Every new mail you send from the Inbox will carry this.');
    } catch (e) {
      setError(errText(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your signature…
      </div>
    );
  }

  const editingSomeoneElse = !!editingId;
  const editingName = editingSomeoneElse
    ? (people.find((p) => p.id === editingId)?.full_name || 'this person')
    : (me.full_name || 'you');

  return (
    <div className="space-y-5" data-testid="signature-settings">
      {/* ---------------------------------------------------------- header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <PenLine className="w-4 h-4 text-[#b08d36]" />
            Email signature
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            This is the little block of text that goes at the bottom of your emails —
            your name, what you do, and how to reach you. Write it once here and the
            system adds it to every message you send from the Inbox. You never have to
            type it again.
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                     bg-[#2e3a68] text-white hover:bg-[#26305a] disabled:opacity-60"
          data-testid="sig-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-xs">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 text-emerald-700 text-xs">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{notice}</span>
        </div>
      )}

      {/* ------------------------------------------------- whose signature */}
      {isManager && people.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border border-slate-200 bg-white">
          <Users className="w-4 h-4 text-slate-400" />
          <span className="text-xs text-slate-600">Whose signature am I editing?</span>
          <select
            value={editingId}
            onChange={(e) => pickPerson(e.target.value)}
            className="text-sm px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-800"
            data-testid="sig-person"
          >
            <option value="">Mine ({me.full_name || me.email})</option>
            {people.filter((p) => p.id !== me.id).map((p) => (
              <option key={p.id} value={p.id}>
                {p.full_name || p.email}{p.has_signature ? '' : ' — none yet'}
              </option>
            ))}
          </select>
          {editingSomeoneElse && (
            <span className="text-[11px] text-amber-700 bg-amber-50 px-2 py-1 rounded">
              You are editing {editingName}&rsquo;s signature, not your own.
            </span>
          )}
        </div>
      )}

      {/* --------------------------------------------------------- on / off */}
      <label className="flex items-center gap-2.5 p-3 rounded-lg border border-slate-200 bg-white cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => { setEnabled(e.target.checked); setDirty(true); }}
          className="w-4 h-4"
          data-testid="sig-enabled"
        />
        <span className="text-sm text-slate-700">
          Add this signature to my emails automatically
          <span className="block text-[11px] text-slate-400">
            Turn this off and your emails go out with nothing at the bottom.
          </span>
        </span>
      </label>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* ------------------------------------------------------ left: make */}
        <div className="space-y-4">
          <div className="p-4 rounded-lg border border-slate-200 bg-white space-y-3">
            <div className="text-xs font-semibold text-slate-700">
              1. Tell it about you
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Your name" value={details.full_name}
                     placeholder="Ruhshad Daruwalla"
                     onChange={(v) => setDetails({ ...details, full_name: v })} />
              <Field label="What you do" value={details.job_title}
                     placeholder="Chief Executive Officer"
                     onChange={(v) => setDetails({ ...details, job_title: v })} />
              <Field label="Phone" value={details.phone}
                     placeholder="+91 98200 00000"
                     onChange={(v) => setDetails({ ...details, phone: v })} />
              <Field label="Email people should reply to" value={details.email}
                     placeholder="care@daruwallas.com"
                     onChange={(v) => setDetails({ ...details, email: v })} />
              <Field label="Website (optional)" value={details.website}
                     placeholder="https://daruwallas.com"
                     onChange={(v) => setDetails({ ...details, website: v })} />
            </div>

            <div className="text-xs font-semibold text-slate-700 pt-1">
              2. Get a signature
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button" onClick={showLayouts} disabled={picking}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium
                           border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                data-testid="sig-layouts"
              >
                {picking ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <RotateCcw className="w-3.5 h-3.5" />}
                Show me three ready-made ones
              </button>
              <button
                type="button" onClick={draftWithAi} disabled={drafting}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium
                           bg-[#c9a84c] text-white hover:bg-[#b08d36] disabled:opacity-60"
                data-testid="sig-draft"
              >
                {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Sparkles className="w-3.5 h-3.5" />}
                Draft one for me
              </button>
            </div>
            <p className="text-[11px] text-slate-400">
              Nothing is saved until you press Save. You can change every word afterwards.
            </p>

            {aiNote && (
              <div className="flex items-start gap-2 text-[11px] text-slate-500">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>{aiNote}</span>
              </div>
            )}

            {options.length > 0 && (
              <div className="grid gap-2 sm:grid-cols-3">
                {options.map((o) => (
                  <OptionCard key={o.key} option={o} active={chosen === o.key} onPick={pickOption} />
                ))}
              </div>
            )}
          </div>

          {/* ------------------------------------------------- the editor */}
          <div className="rounded-lg border border-slate-200 bg-white">
            <button
              type="button"
              onClick={() => setAdvanced(!advanced)}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50"
              data-testid="sig-advanced"
            >
              {advanced ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              3. Change the wording yourself
              <span className="ml-auto text-[11px] font-normal text-slate-400">
                {advanced ? 'Hide' : 'Open'}
              </span>
            </button>
            {advanced && (
              <div className="px-3 pb-3 space-y-2">
                <p className="text-[11px] text-slate-500">
                  This box holds your signature. Change any of the words between the
                  angle brackets and watch the preview on the right update. If it ever
                  goes wrong, press one of the buttons above to start again.
                </p>
                <textarea
                  value={html}
                  onChange={(e) => { setHtml(e.target.value); setDirty(true); }}
                  rows={12}
                  spellCheck={false}
                  placeholder="Your signature will appear here once you pick one above."
                  className="w-full text-xs font-mono px-3 py-2 rounded-lg border border-slate-200
                             bg-slate-50 text-slate-800 focus:outline-none focus:border-slate-400"
                  data-testid="sig-html"
                />
                {cleaned && (
                  <div className="flex items-start gap-2 text-[11px] text-amber-700">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>
                      Some of what you pasted was removed for safety (scripts and
                      similar are never sent). The preview shows what will actually go out.
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* --------------------------------------------------- right: preview */}
        <div className="space-y-3">
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-100 text-xs font-semibold text-slate-700">
              What the person receiving your email will see
            </div>
            {previewHtml
              ? <SignaturePreview html={previewHtml} />
              : (
                <div className="p-6 text-xs text-slate-400">
                  Pick a signature on the left and it will show up here.
                </div>
              )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-100 text-xs font-semibold text-slate-700">
              And for people whose email shows plain text only
            </div>
            <pre className="p-3 text-[11px] text-slate-600 whitespace-pre-wrap font-mono max-h-64 overflow-auto">
              {previewText || '—'}
            </pre>
          </div>

          <div className="flex items-start gap-2 p-3 rounded-lg bg-[#faf7f0] text-[#7a5f1e] text-[11px]">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              Replies keep the older messages underneath. Your signature is added
              just once, above that older conversation — never twice, and never at
              the very bottom where nobody looks.
            </span>
          </div>

          {dirty && (
            <div className="text-[11px] text-amber-700">
              You have changes that are not saved yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
